import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Download, Copy, Printer } from 'lucide-react';
import { statusApi } from '../../services/api';
import { LabelPrintSettings } from '../../types';
import {
  LABEL_PRESETS,
  CUSTOM_KEY,
  DEFAULT_PRESET_KEY,
  MIN_LABEL_IN,
  MAX_LABEL_IN,
  clamp,
  findPreset,
  isKnownPresetKey,
  sanitizeDimension,
  computeLabelLayout,
  escapeHtml,
} from '../../utils/labelPrint';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  tenantSlug: string;
  squareItemId: string;
  organizationId: string;
}

const DEFAULT_WIDTH_IN = findPreset(DEFAULT_PRESET_KEY).width;
const DEFAULT_HEIGHT_IN = findPreset(DEFAULT_PRESET_KEY).height;

/**
 * Modal component for displaying and downloading QR codes for products.
 *
 * The QR code encodes a URL that redirects to the organization's Square online store
 * product page when scanned.
 */
export const QRCodeModal: React.FC<QRCodeModalProps> = ({
  isOpen,
  onClose,
  productName,
  tenantSlug,
  squareItemId,
  organizationId,
}) => {
  const qrRef = useRef<HTMLDivElement>(null);

  const [selectedPresetKey, setSelectedPresetKey] = useState<string>(DEFAULT_PRESET_KEY);
  const [customWidth, setCustomWidth] = useState<string>(String(DEFAULT_WIDTH_IN));
  const [customHeight, setCustomHeight] = useState<string>(String(DEFAULT_HEIGHT_IN));
  const [loadFailed, setLoadFailed] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  // Guards the org's saved-size GET from clobbering a selection the user
  // already made while that GET was still in flight (Dutch, PR #90 fix
  // round): the user's own choice always wins over a slow response.
  const userTouchedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    userTouchedRef.current = false;
    setSaveFailed(false);
    let cancelled = false;

    statusApi.getLabelPrintSettings(organizationId).then((res) => {
      if (cancelled || userTouchedRef.current) return;
      if (res.success && res.data) {
        setLoadFailed(false);
        const { width_in, height_in, preset } = res.data;
        if (isKnownPresetKey(preset)) {
          setSelectedPresetKey(preset);
        } else {
          setSelectedPresetKey(CUSTOM_KEY);
          setCustomWidth(String(sanitizeDimension(width_in, DEFAULT_WIDTH_IN)));
          setCustomHeight(String(sanitizeDimension(height_in, DEFAULT_HEIGHT_IN)));
        }
      } else if (res.success) {
        // success with no data: the org has never printed a label. Not a
        // failure, just empty; the default stands with no error note.
        setLoadFailed(false);
        setSelectedPresetKey(DEFAULT_PRESET_KEY);
      } else {
        setLoadFailed(true);
        setSelectedPresetKey(DEFAULT_PRESET_KEY);
      }
    }).catch(() => {
      if (cancelled || userTouchedRef.current) return;
      setLoadFailed(true);
      setSelectedPresetKey(DEFAULT_PRESET_KEY);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, organizationId]);

  if (!isOpen) return null;

  const qrUrl = `https://${tenantSlug}.smartcatalog.app/go/${squareItemId}`;

  const getSelectedDimensions = (): LabelPrintSettings => {
    if (selectedPresetKey === CUSTOM_KEY) {
      const width_in = clamp(parseFloat(customWidth) || MIN_LABEL_IN, MIN_LABEL_IN, MAX_LABEL_IN);
      const height_in = clamp(parseFloat(customHeight) || MIN_LABEL_IN, MIN_LABEL_IN, MAX_LABEL_IN);
      return { width_in, height_in, preset: null };
    }
    const preset = findPreset(selectedPresetKey);
    return { width_in: preset.width, height_in: preset.height, preset: preset.key };
  };

  const handlePresetChange = (value: string) => {
    userTouchedRef.current = true;
    setSelectedPresetKey(value);
  };

  const handleCustomWidthChange = (value: string) => {
    userTouchedRef.current = true;
    setCustomWidth(value);
  };

  const handleCustomHeightChange = (value: string) => {
    userTouchedRef.current = true;
    setCustomHeight(value);
  };

  const handleCustomWidthBlur = () => {
    const value = clamp(parseFloat(customWidth) || MIN_LABEL_IN, MIN_LABEL_IN, MAX_LABEL_IN);
    setCustomWidth(String(value));
  };

  const handleCustomHeightBlur = () => {
    const value = clamp(parseFloat(customHeight) || MIN_LABEL_IN, MIN_LABEL_IN, MAX_LABEL_IN);
    setCustomHeight(String(value));
  };

  const handleDownload = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    // Create a canvas to convert SVG to PNG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width * 2; // 2x for better quality
      canvas.height = img.height * 2;
      ctx.scale(2, 2);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-${productName.replace(/\s+/g, '-').toLowerCase()}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      // Could add a toast notification here
      alert('URL copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handlePrint = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const { width_in, height_in, preset } = getSelectedDimensions();

    // Persist the org's last-used size for next time. Printing must never
    // wait on this, and a rejected or unreachable PUT must not block or
    // break the label the user is standing at the printer for: it
    // degrades to a small inline note instead (never console logging).
    setSaveFailed(false);
    void statusApi.updateLabelPrintSettings(organizationId, { width_in, height_in, preset })
      .catch(() => setSaveFailed(true));

    const svgData = new XMLSerializer().serializeToString(svg);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the QR code');
      return;
    }

    const { marginIn, qrSizeIn } = computeLabelLayout(width_in, height_in);
    const titleMaxWidthIn = Math.max(0.2, width_in - marginIn * 2);
    const safeProductName = escapeHtml(productName);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${safeProductName}</title>
          <style>
            @page {
              size: ${width_in}in ${height_in}in;
              margin: 0;
            }
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              width: ${width_in}in;
              height: ${height_in}in;
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              box-sizing: border-box;
            }
            h2 {
              margin: 0 0 ${marginIn}in 0;
              font-size: 12pt;
              color: #1f2937;
              text-align: center;
              max-width: ${titleMaxWidthIn}in;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .qr-wrap svg {
              width: ${qrSizeIn}in;
              height: ${qrSizeIn}in;
              display: block;
            }
          </style>
        </head>
        <body>
          <h2>${safeProductName}</h2>
          <div class="qr-wrap">
            ${svgData}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();

    // onload and the setTimeout fallback below can both fire: guard so
    // print() only ever runs once (Wren, PR #90 fix round).
    let printed = false;
    const firePrint = () => {
      if (printed) return;
      printed = true;
      printWindow.print();
    };

    // Wait for content to load, then print
    printWindow.onload = () => {
      firePrint();
    };

    // Fallback: print after a short delay if onload doesn't fire
    setTimeout(() => {
      if (!printWindow.closed) {
        firePrint();
      }
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Product QR Code</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Product Name */}
        <p className="text-sm text-gray-600 mb-4 text-center">{productName}</p>

        {/* QR Code */}
        <div className="flex justify-center mb-4" ref={qrRef}>
          <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
            <QRCodeSVG
              value={qrUrl}
              size={200}
              level="H" // High error correction
              includeMargin={false}
            />
          </div>
        </div>

        {/* URL */}
        <p className="text-xs text-gray-500 text-center mb-4 break-all px-4">
          {qrUrl}
        </p>

        {/* Label Size */}
        <div className="mb-4">
          <label htmlFor="qr-label-size" className="block text-xs font-medium text-gray-600 mb-1">
            Label Size
          </label>
          <select
            id="qr-label-size"
            value={selectedPresetKey}
            onChange={(e) => handlePresetChange(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5"
          >
            {LABEL_PRESETS.map((preset) => (
              <option key={preset.key} value={preset.key}>
                {preset.label}
              </option>
            ))}
            <option value={CUSTOM_KEY}>Custom</option>
          </select>

          {selectedPresetKey === CUSTOM_KEY && (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="number"
                min={MIN_LABEL_IN}
                max={MAX_LABEL_IN}
                step="0.125"
                value={customWidth}
                onChange={(e) => handleCustomWidthChange(e.target.value)}
                onBlur={handleCustomWidthBlur}
                aria-label="Custom label width in inches"
                className="w-20 text-sm border border-gray-300 rounded-md px-2 py-1.5"
              />
              <span className="text-xs text-gray-500">x</span>
              <input
                type="number"
                min={MIN_LABEL_IN}
                max={MAX_LABEL_IN}
                step="0.125"
                value={customHeight}
                onChange={(e) => handleCustomHeightChange(e.target.value)}
                onBlur={handleCustomHeightBlur}
                aria-label="Custom label height in inches"
                className="w-20 text-sm border border-gray-300 rounded-md px-2 py-1.5"
              />
              <span className="text-xs text-gray-500">in</span>
            </div>
          )}

          {loadFailed && (
            <p className="text-xs text-amber-600 mt-1">Couldn&apos;t load saved size, using default.</p>
          )}
          {saveFailed && (
            <p className="text-xs text-amber-600 mt-1">Couldn&apos;t save size preference.</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handlePrint}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
          <button
            onClick={handleCopyUrl}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Copy className="w-4 h-4" />
            Copy URL
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
